import type {
  ShiftDay,
  ShiftDetail,
  ShiftStatus,
  ShiftTemplate,
  UpdateShiftPayload,
} from '@/types/shift.types';

export interface ShiftDraftRow {
  key: string;
  employeeUuid: string | null;
  employeeName: string | null;
  position: string | null;
  plannedStart: string;
  plannedEnd: string;
  paymentSchemeTemplateUuid: string | null;
  /** Пришёл с сервера. У open такие строки остаются в теле сохранения. */
  fromServer: boolean;
}

export interface ShiftDraft {
  uuid: string;
  date: string;
  status: ShiftStatus;
  responsibleEmployeeUuid: string | null;
  selectedTemplateUuid: string | null;
  rows: ShiftDraftRow[];
}

export interface ShiftRowErrors {
  employee?: string;
  plannedStart?: string;
  plannedEnd?: string;
  scheme?: string;
}

export interface ShiftDraftErrors {
  form?: string;
  responsible?: string;
  rows: Record<string, ShiftRowErrors>;
}

export const emptyShiftDraftErrors = (): ShiftDraftErrors => ({ rows: {} });

/** Допустимое плановое время: часы работы центра на дату. В выходной границы нет. */
export interface PlannedTimeBounds {
  fromMinutes: number;
  toMinutes: number;
  label: string;
}

export const toClock = (value: string | null | undefined): string => {
  if (!value) return '';
  const match = /^(\d{2}):(\d{2})/.exec(value);
  return match ? `${match[1]}:${match[2]}` : '';
};

/** «10» и «10:» становятся 10:00, неполный ввод минут дополняется нулями. */
export const completeClock = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const match = /^(\d{1,2})(?::(\d{0,2}))?$/.exec(trimmed);
  if (!match) return trimmed;

  const hours = Number(match[1]);
  const minutesPart = match[2];
  if (hours > 23) return trimmed;
  if (minutesPart === undefined || minutesPart === '') {
    return `${String(hours).padStart(2, '0')}:00`;
  }
  const minutes = Number(minutesPart);
  if (minutes > 59) return trimmed;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const clockToMinutes = (value: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(completeClock(value));
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

const rowKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `row-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const emptyDraftRow = (): ShiftDraftRow => ({
  key: rowKey(),
  employeeUuid: null,
  employeeName: null,
  position: null,
  plannedStart: '',
  plannedEnd: '',
  paymentSchemeTemplateUuid: null,
  fromServer: false,
});

export const draftFromDetail = (detail: ShiftDetail): ShiftDraft => ({
  uuid: detail.uuid,
  date: detail.date.slice(0, 10),
  status: detail.status,
  responsibleEmployeeUuid: detail.responsible_employee_uuid,
  selectedTemplateUuid: detail.template_uuid,
  rows: detail.members.map((member) => ({
    key: member.uuid,
    employeeUuid: member.employee_uuid,
    employeeName: member.name,
    position: member.position,
    plannedStart: toClock(member.planned_start),
    plannedEnd: toClock(member.planned_end),
    paymentSchemeTemplateUuid: member.payment_scheme_template_uuid,
    fromServer: true,
  })),
});

const rowFromTemplate = (
  row: ShiftTemplate['rows'][number],
  existing?: ShiftDraftRow,
): ShiftDraftRow => ({
  key: existing?.key ?? row.uuid,
  employeeUuid: row.employee_uuid,
  employeeName: row.name,
  position: row.position,
  plannedStart: toClock(row.planned_start),
  plannedEnd: toClock(row.planned_end),
  paymentSchemeTemplateUuid: row.payment_scheme_template_uuid,
  fromServer: existing?.fromServer ?? false,
});

/**
 * «Применить» заполняет черновик и не ходит на сервер.
 * «Без шаблона» (template = null) состав не меняет.
 * У open люди, уже пришедшие с сервера, остаются в черновике.
 */
export const applyTemplateToDraft = (
  draft: ShiftDraft,
  template: ShiftTemplate | null,
): ShiftDraft => {
  if (draft.status === 'closed') return draft;
  if (!template) {
    return { ...draft, selectedTemplateUuid: null };
  }

  if (draft.status === 'open') {
    const merged = draft.rows.map((row) => {
      const fromTemplate = template.rows.find(
        (templateRow) => templateRow.employee_uuid === row.employeeUuid,
      );
      return fromTemplate ? rowFromTemplate(fromTemplate, row) : row;
    });
    for (const templateRow of template.rows) {
      const already = merged.some((row) => row.employeeUuid === templateRow.employee_uuid);
      if (!already) merged.push(rowFromTemplate(templateRow));
    }
    return {
      ...draft,
      selectedTemplateUuid: template.uuid,
      responsibleEmployeeUuid: template.responsible_employee_uuid,
      rows: merged,
    };
  }

  return {
    ...draft,
    selectedTemplateUuid: template.uuid,
    responsibleEmployeeUuid: template.responsible_employee_uuid,
    rows: template.rows.map((row) => rowFromTemplate(row)),
  };
};

export const addDraftRow = (draft: ShiftDraft): ShiftDraft => {
  if (draft.status === 'closed') return draft;
  return { ...draft, rows: [...draft.rows, emptyDraftRow()] };
};

/** Строка сразу с сотрудником: имя и роль берутся из карточки, время и схема пустые. */
export const addDraftEmployee = (
  draft: ShiftDraft,
  employeeUuid: string,
  person?: { name: string | null; position: string | null },
): ShiftDraft => {
  if (draft.status === 'closed' || !employeeUuid) return draft;
  if (draft.rows.some((row) => row.employeeUuid === employeeUuid)) return draft;
  return {
    ...draft,
    rows: [
      ...draft.rows,
      {
        ...emptyDraftRow(),
        employeeUuid,
        employeeName: person?.name ?? null,
        position: person?.position ?? null,
      },
    ],
  };
};

/** Корзина снимает строку только у created. У open и closed черновик не меняется. */
export const removeDraftRow = (draft: ShiftDraft, key: string): ShiftDraft => {
  if (draft.status !== 'created') return draft;
  return { ...draft, rows: draft.rows.filter((row) => row.key !== key) };
};

export const setDraftRowEmployee = (
  draft: ShiftDraft,
  key: string,
  employeeUuid: string | null,
): ShiftDraft => {
  if (draft.status === 'closed') return draft;
  if (
    employeeUuid &&
    draft.rows.some((row) => row.key !== key && row.employeeUuid === employeeUuid)
  ) {
    return draft;
  }
  return {
    ...draft,
    rows: draft.rows.map((row) =>
      row.key === key
        ? { ...row, employeeUuid, employeeName: null, position: null }
        : row,
    ),
  };
};

export const hasShiftDraftErrors = (errors: ShiftDraftErrors): boolean =>
  Boolean(
    errors.form ||
      errors.responsible ||
      Object.values(errors.rows).some((row) => Object.keys(row).length > 0),
  );

/**
 * Пустая смена без строк и без ответственного — валидна.
 * Если строки есть: сотрудник, оба времени, начало раньше конца, схема, без повторов.
 */
export const validateShiftDraft = (
  draft: ShiftDraft,
  bounds?: PlannedTimeBounds | null,
): ShiftDraftErrors => {
  if (draft.status === 'closed') {
    return { form: 'Закрытую смену нельзя изменить', rows: {} };
  }

  const errors: ShiftDraftErrors = { rows: {} };
  const seen = new Set<string>();

  for (const row of draft.rows) {
    const rowErrors: ShiftRowErrors = {};
    if (!row.employeeUuid) {
      rowErrors.employee = 'Выберите сотрудника';
    } else if (seen.has(row.employeeUuid)) {
      rowErrors.employee = 'Сотрудник уже в смене';
    } else {
      seen.add(row.employeeUuid);
    }

    const start = row.plannedStart ? clockToMinutes(row.plannedStart) : null;
    const end = row.plannedEnd ? clockToMinutes(row.plannedEnd) : null;
    if (!row.plannedStart || start === null) rowErrors.plannedStart = 'Укажите время';
    if (!row.plannedEnd || end === null) rowErrors.plannedEnd = 'Укажите время';
    if (start !== null && end !== null && start >= end) {
      rowErrors.plannedEnd = 'Начало должно быть раньше конца';
    }
    if (bounds && start !== null && (start < bounds.fromMinutes || start > bounds.toMinutes)) {
      rowErrors.plannedStart = `Допустимо ${bounds.label}`;
    }
    if (bounds && end !== null && (end < bounds.fromMinutes || end > bounds.toMinutes)) {
      rowErrors.plannedEnd = rowErrors.plannedEnd ?? `Допустимо ${bounds.label}`;
    }
    if (!row.paymentSchemeTemplateUuid) rowErrors.scheme = 'Выберите схему оплаты';
    if (Object.keys(rowErrors).length > 0) errors.rows[row.key] = rowErrors;
  }

  if (draft.responsibleEmployeeUuid && !seen.has(draft.responsibleEmployeeUuid)) {
    errors.responsible = 'Ответственный должен входить в состав смены';
  }

  return errors;
};

export const completeDraftTimes = (draft: ShiftDraft): ShiftDraft => ({
  ...draft,
  rows: draft.rows.map((row) => ({
    ...row,
    plannedStart: completeClock(row.plannedStart),
    plannedEnd: completeClock(row.plannedEnd),
  })),
});

export const toUpdatePayload = (draft: ShiftDraft): UpdateShiftPayload => ({
  responsible_employee_uuid: draft.responsibleEmployeeUuid,
  members: draft.rows.map((row) => ({
    employee_uuid: row.employeeUuid as string,
    planned_start: completeClock(row.plannedStart),
    planned_end: completeClock(row.plannedEnd),
    payment_scheme_template_uuid: row.paymentSchemeTemplateUuid,
  })),
});

/** Куда вести после 409: редактор существующей смены или остаться на дне. */
export const conflictOpensEditor = (status: ShiftStatus | null | undefined): boolean =>
  status === 'created' || status === 'open';

export const hasShiftRoster = (shift: Pick<ShiftDay, 'members'>): boolean =>
  shift.members.length > 0;

export const hasDraftRoster = (draft: ShiftDraft): boolean =>
  draft.rows.some((row) => Boolean(row.employeeUuid));

/** Пустая created-смена с сервера: при отмене редактора её можно убрать. */
export const shouldAbandonEmptyCreatedShift = (
  draft: ShiftDraft,
  initialServerMemberCount: number,
): boolean =>
  draft.status === 'created' &&
  initialServerMemberCount === 0 &&
  !hasDraftRoster(draft);

export const emptyShiftOpensEditor = (shift: ShiftDay | undefined): boolean =>
  Boolean(shift && !hasShiftRoster(shift) && conflictOpensEditor(shift.status));
